import React, { useEffect, useState } from 'react'
import { AuthApiError, previewBedrockConsoleRegistration } from '../../api/utopiaAuthApi'
import styles from './utopiaLanding.module.css'

type Props = {
  idPrefix: string
  code: string
  onCodeChange: (code: string) => void
  username: string
  onUsernameResolved: (username: string) => void
  onBack: () => void
  onError: (message: string | null) => void
}

export default function UtopiaBedrockConsoleRegister ({
  idPrefix,
  code,
  onCodeChange,
  username,
  onUsernameResolved,
  onBack,
  onError,
}: Props) {
  const [platform, setPlatform] = useState<string | null>(null)
  const [checking, setChecking] = useState(false)
  const [codeFeedback, setCodeFeedback] = useState<string | null>(null)
  const [codeFeedbackOk, setCodeFeedbackOk] = useState(false)

  useEffect(() => {
    const normalized = code.trim().toUpperCase()
    if (normalized.length !== 8) {
      setPlatform(null)
      setCodeFeedback(null)
      setCodeFeedbackOk(false)
      onUsernameResolved('')
      return
    }
    let cancelled = false
    setChecking(true)
    setCodeFeedback(null)
    setCodeFeedbackOk(false)
    void previewBedrockConsoleRegistration(normalized)
      .then(result => {
        if (cancelled) return
        if (result.username) {
          onUsernameResolved(result.username)
          setPlatform(result.platform ?? null)
          setCodeFeedbackOk(true)
          setCodeFeedback(`Pseudo détecté : ${result.username}`)
          onError(null)
        } else {
          onUsernameResolved('')
          setPlatform(null)
          setCodeFeedbackOk(false)
          setCodeFeedback('Réponse invalide du serveur. Réessayez.')
          onError('Réponse invalide du serveur. Réessayez.')
        }
      })
      .catch(err => {
        if (cancelled) return
        setPlatform(null)
        onUsernameResolved('')
        const message = err instanceof AuthApiError ? err.message : String(err)
        setCodeFeedbackOk(false)
        setCodeFeedback(message)
        onError(message)
      })
      .finally(() => {
        if (!cancelled) setChecking(false)
      })
    return () => {
      cancelled = true
    }
  }, [code, onError, onUsernameResolved])

  return (
    <div className={styles.authBedrockConsolePanel}>
      <p className={styles.authBedrockConsoleTitle}>Inscription Bedrock Console</p>
      <p className={styles.authBedrockConsoleHint}>
        Rejoignez le serveur en Bedrock sur console (Xbox, PlayStation ou Switch).
        Un code à 8 caractères s&apos;affiche en jeu. Saisissez-le ici depuis le même réseau
        (même box Internet que l&apos;appareil qui a rejoint le serveur).
      </p>
      <div className={styles.authField}>
        <label className={styles.authLabel} htmlFor={`${idPrefix}-console-code`}>
          Code console
        </label>
        <input
          id={`${idPrefix}-console-code`}
          className={styles.authInput}
          value={code}
          onChange={e => {
            onCodeChange(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))
            onError(null)
          }}
          placeholder="AB12CD34"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={8}
          minLength={8}
          required
        />
        {checking ? (
          <p className={styles.authHint}>Vérification du code…</p>
        ) : null}
        {codeFeedback && !checking ? (
          <p
            className={codeFeedbackOk ? styles.authBedrockConsoleSuccess : styles.authBedrockConsoleError}
            role="status"
          >
            {codeFeedback}
          </p>
        ) : null}
      </div>
      {platform && username ? (
        <p className={styles.authBedrockConsoleMeta}>
          Plateforme : <strong>{platform}</strong>
        </p>
      ) : null}
      <button type="button" className={styles.authBedrockConsoleBack} onClick={onBack}>
        ← Inscription classique ou Microsoft
      </button>
    </div>
  )
}
